import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollFadeWrapper } from "@/components/ui/scroll-fade-wrapper";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LogOut, Stethoscope, Users, AlertTriangle, Clock, CheckCircle, Send, ArrowRight, FileText, Calendar, Pill, UserCheck, ShieldAlert, DollarSign, Mail, MessageSquare, PanelLeft, FlaskConical } from "lucide-react";
import { ChevronRight as BreadcrumbChevron } from "lucide-react";
import { getGlp1Flags } from "@/lib/glp1-screening";
import { differenceInDays, parseISO } from "date-fns";
import WorkflowProgressBar from "@/components/WorkflowProgressBar";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/Navbar";
import ProviderAvailability from "@/components/ProviderAvailability";
import ProviderAppointments from "@/components/ProviderAppointments";
import ProviderCalendarView from "@/components/ProviderCalendarView";
import PeptideReference from "@/components/PeptideReference";
import SendPrescription from "@/components/SendPrescription";
import ProviderPayoutView from "@/components/ProviderPayoutView";
import ContractorTaxInfo from "@/components/ContractorTaxInfo";
import { useAuditLog } from "@/hooks/useAuditLog";
import ProviderOnboardingChecklist from "@/components/ProviderOnboardingChecklist";
import ProviderOnboardingGate from "@/components/ProviderOnboardingGate";
import MessageInbox from "@/components/MessageInbox";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import SessionFinalizationChecklist from "@/components/SessionFinalizationChecklist";
import ProviderSidebar from "@/components/sidebar/ProviderSidebar";
import ProviderTodaySchedule from "@/components/ProviderTodaySchedule";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import ErrorBoundary from "@/components/ErrorBoundary";

interface Assessment {
  id: string;
  user_id: string;
  goal: string | null;
  peptide_interest: string | null;
  medications: string | null;
  allergies: string | null;
  conditions: string | null;
  additional_info: string | null;
  status: string;
  triage_level: string | null;
  created_at: string;
  updated_at: string;
}

interface ProviderNote {
  id: string;
  note: string;
  provider_id: string;
  created_at: string;
}

interface PatientProfile {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
}

interface ProviderProfile {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  title_prefix?: string | null;
}

/** Format a provider display name using their title prefix (Dr., NP, PA-C) */
const formatProviderName = (profile: { first_name?: string | null; last_name?: string | null; title_prefix?: string | null } | null | undefined): string => {
  if (!profile) return "Provider";
  const name = profile.last_name || profile.first_name || "Unknown";
  const prefix = profile.title_prefix;
  return prefix ? `${prefix} ${name}` : name;
};

const statusConfig: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  reviewed: "bg-blue-100 text-blue-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
};


/** Returns the next logical step for the provider based on the assessment state */
const getNextStep = (assessment: Assessment, hasNotes: boolean, hasPrescription?: boolean, hasProvider?: boolean): { label: string; description: string; icon: typeof FileText } => {
  if (!hasProvider) {
    return { label: "Assign Provider", description: "Assign a provider to this patient", icon: UserCheck };
  }
  if (assessment.status === "pending") {
    return { label: "Review Assessment", description: "Review clinical details and update status", icon: FileText };
  }
  if (assessment.status === "reviewed" && !hasNotes) {
    return { label: "Add Clinical Notes", description: "Document your findings and recommendations", icon: FileText };
  }
  if (assessment.status === "reviewed") {
    return { label: "Approve or Schedule", description: "Approve for treatment or schedule a consultation", icon: CheckCircle };
  }
  if (assessment.status === "approved" && !hasPrescription) {
    return { label: "Write Prescription", description: "Create and send prescription to pharmacy", icon: Pill };
  }
  return { label: "Complete", description: "This assessment has been fully processed", icon: CheckCircle };
};

const ProviderDashboard = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [selectedAssessment, setSelectedAssessment] = useState<Assessment | null>(null);
  const [notes, setNotes] = useState<ProviderNote[]>([]);
  const [newNote, setNewNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [dashboardTab, setDashboardTab] = useState("schedule");
  const TAB_LABELS: Record<string, string> = {
    schedule: "Schedule",
    patients: "Patients",
    appointments: "Appointments",
    payouts: "Payouts",
    availability: "Availability",
    messages: "Messages",
    reference: "Reference",
    tax: "Tax Info",
  };
  const [profile, setProfile] = useState<{ first_name: string | null; last_name: string | null; title_prefix: string | null; overdue_threshold_days: number } | null>(null);
  const [overdueThreshold, setOverdueThreshold] = useState(3);
  const [patientProfiles, setPatientProfiles] = useState<Record<string, PatientProfile>>({});
  const [reviewerMap, setReviewerMap] = useState<Record<string, string>>({});
  const [prescriptionMap, setPrescriptionMap] = useState<Record<string, boolean>>({});
  const [assignedProviderMap, setAssignedProviderMap] = useState<Record<string, string | null>>({});
  const [lastCheckupMap, setLastCheckupMap] = useState<Record<string, string | null>>({});
  const [consultationCompletedMap, setConsultationCompletedMap] = useState<Record<string, boolean>>({});
  const [appointmentScheduledMap, setAppointmentScheduledMap] = useState<Record<string, boolean>>({});
  const [scheduledTimeMap, setScheduledTimeMap] = useState<Record<string, string>>({});
  const [allProviders, setAllProviders] = useState<ProviderProfile[]>([]);
  const [providerCredentialMap, setProviderCredentialMap] = useState<Record<string, { license_expiry: string; credential_status: string }>>({});
  const [sendingReminder, setSendingReminder] = useState<string | null>(null);
  const [lastReminderMap, setLastReminderMap] = useState<Record<string, string>>({});
  const [prescribeAssessment, setPrescribeAssessment] = useState<Assessment | null>(null);
  const { logAccess } = useAuditLog();

  const assignSelfToPatient = async (patientUserId: string) => {
    if (!user) return;
    const { error } = await supabase
      .from("profiles")
      .update({ assigned_provider_id: user.id })
      .eq("user_id", patientUserId);
    if (error) {
      toast({ title: "Error", description: "Failed to assign provider.", variant: "destructive" });
    } else {
      setAssignedProviderMap((prev) => ({ ...prev, [patientUserId]: user.id }));
      toast({ title: "Assigned", description: "You are now this patient's provider." });
    }
  };

  const recordCheckup = async (patientUserId: string, date: string) => {
    if (!user) return;
    const { error } = await supabase
      .from("profiles")
      .update({ last_checkup_date: date, assigned_provider_id: user.id })
      .eq("user_id", patientUserId);
    if (error) {
      toast({ title: "Error", description: "Failed to record checkup.", variant: "destructive" });
    } else {
      setAssignedProviderMap((prev) => ({ ...prev, [patientUserId]: user.id }));
      setLastCheckupMap((prev) => ({ ...prev, [patientUserId]: date }));
      toast({ title: "Checkup recorded", description: `Last checkup set to ${new Date(date).toLocaleDateString()}.` });
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchAssessments();
    supabase.from("profiles").select("first_name, last_name, title_prefix, overdue_threshold_days").eq("user_id", user.id).single().then(({ data }) => {
      if (data) {
        setProfile(data as any);
        setOverdueThreshold((data as any).overdue_threshold_days ?? 3);
      }
    });
    // Fetch all providers for the assignment dropdown + their credential status
    supabase.from("user_roles").select("user_id").eq("role", "provider").then(async ({ data: roles }) => {
      if (roles && roles.length > 0) {
        const providerIds = roles.map((r) => r.user_id);
        const [{ data: provProfiles }, { data: creds }] = await Promise.all([
          supabase.from("profiles").select("user_id, first_name, last_name, title_prefix").in("user_id", providerIds),
          supabase.from("provider_credentials" as any).select("user_id, license_expiry, credential_status").in("user_id", providerIds),
        ]);
        if (provProfiles) setAllProviders(provProfiles);
        if (creds) {
          const credMap: Record<string, { license_expiry: string; credential_status: string }> = {};
          (creds as any[]).forEach((c: any) => {
            credMap[c.user_id] = { license_expiry: c.license_expiry, credential_status: c.credential_status };
          });
          setProviderCredentialMap(credMap);
        }
      }
    });
  }, [user]);

  const fetchAssessments = async () => {
    const { data } = await supabase
      .from("health_assessments")
      .select("*")
      .order("created_at", { ascending: false });
    if (data) {
      setAssessments(data);
      logAccess({ action: "view_assessment", resource_type: "health_assessment", metadata: { count: data.length, role: "provider" } });

      // Fetch patient profiles for all assessments
      const userIds = [...new Set(data.map((a) => a.user_id))];
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("user_id, first_name, last_name, assigned_provider_id, last_checkup_date")
          .in("user_id", userIds);
        if (profiles) {
          const map: Record<string, PatientProfile> = {};
          const provMap: Record<string, string | null> = {};
          const checkMap: Record<string, string | null> = {};
          profiles.forEach((p: any) => {
            map[p.user_id] = p;
            provMap[p.user_id] = p.assigned_provider_id;
            checkMap[p.user_id] = p.last_checkup_date;
          });
          setPatientProfiles(map);
          setAssignedProviderMap(provMap);
          setLastCheckupMap(checkMap);
        }
      }

      // Fetch reviewer info — first provider note per assessment tells us who reviewed
      const assessmentIds = data.map((a) => a.id);
      if (assessmentIds.length > 0) {
        const { data: allNotes } = await supabase
          .from("provider_notes")
          .select("assessment_id, provider_id, created_at")
          .in("assessment_id", assessmentIds)
          .order("created_at", { ascending: true });
        if (allNotes && allNotes.length > 0) {
          // Get the first note's provider_id per assessment
          const firstReviewer: Record<string, string> = {};
          const providerIds = new Set<string>();
          allNotes.forEach((n) => {
            if (!firstReviewer[n.assessment_id]) {
              firstReviewer[n.assessment_id] = n.provider_id;
              providerIds.add(n.provider_id);
            }
          });

          // Fetch provider names
          if (providerIds.size > 0) {
            const { data: providerProfiles } = await supabase
              .from("profiles")
              .select("user_id, first_name, last_name, title_prefix")
              .in("user_id", [...providerIds]);
            if (providerProfiles) {
              const provMap: Record<string, string> = {};
              providerProfiles.forEach((p) => {
                provMap[p.user_id] = formatProviderName(p as any);
              });
              const reviewerResult: Record<string, string> = {};
              Object.entries(firstReviewer).forEach(([aId, pId]) => {
                reviewerResult[aId] = provMap[pId] || "Provider";
              });
              setReviewerMap(reviewerResult);
            }
          }
        }
      }

      // Fetch existing prescriptions to mark completed workflows
      const rxAssessmentIds = data.map((a) => a.id);
      if (rxAssessmentIds.length > 0) {
        const { data: prescriptions } = await supabase
          .from("prescriptions")
          .select("assessment_id")
          .in("assessment_id", rxAssessmentIds)
          .not("assessment_id", "is", null);
        if (prescriptions) {
          const pMap: Record<string, boolean> = {};
          prescriptions.forEach((p) => {
            if (p.assessment_id) pMap[p.assessment_id] = true;
          });
          setPrescriptionMap((prev) => ({ ...prev, ...pMap }));
        }
      }

      // Fetch completed and scheduled consultations per patient
      if (userIds.length > 0) {
        const [{ data: completedAppts }, { data: scheduledAppts }] = await Promise.all([
          supabase
            .from("appointments")
            .select("patient_id")
            .in("patient_id", userIds)
            .eq("status", "completed"),
          supabase
            .from("appointments")
            .select("patient_id, start_time")
            .in("patient_id", userIds)
            .in("status", ["scheduled", "confirmed"]),
        ]);
        if (completedAppts) {
          const cMap: Record<string, boolean> = {};
          completedAppts.forEach((a) => { cMap[a.patient_id] = true; });
          setConsultationCompletedMap(cMap);
        }
        if (scheduledAppts) {
          const sMap: Record<string, boolean> = {};
          const tMap: Record<string, string> = {};
          scheduledAppts.forEach((a: any) => {
            sMap[a.patient_id] = true;
            if (!tMap[a.patient_id] || a.start_time < tMap[a.patient_id]) {
              tMap[a.patient_id] = a.start_time;
            }
          });
          setAppointmentScheduledMap(sMap);
          setScheduledTimeMap(tMap);
        }

        // Fetch last scheduling reminder sent per patient
        const { data: reminderLogs } = await supabase
          .from("email_send_log")
          .select("recipient_email, created_at, metadata")
          .eq("template_name", "schedule-consultation-reminder")
          .eq("status", "sent")
          .order("created_at", { ascending: false });
        if (reminderLogs && reminderLogs.length > 0) {
          // We need to map email → user_id; use profiles we already have
          const { data: userEmails } = await supabase.rpc("get_user_emails");
          if (userEmails) {
            const emailToUser: Record<string, string> = {};
            userEmails.forEach((e: any) => { emailToUser[e.email] = e.user_id; });
            const rMap: Record<string, string> = {};
            reminderLogs.forEach((log: any) => {
              const uid = emailToUser[log.recipient_email];
              if (uid && !rMap[uid]) {
                rMap[uid] = log.created_at;
              }
            });
            setLastReminderMap(rMap);
          }
        }
      }
    }
  };

  const getPatientName = (userId: string) => {
    const p = patientProfiles[userId];
    if (!p) return "Unknown Patient";
    if (p.first_name || p.last_name) {
      return `${p.first_name || ""} ${p.last_name || ""}`.trim();
    }
    return "Unknown Patient";
  };

  const openAssessment = async (assessment: Assessment) => {
    setSelectedAssessment(assessment);
    setNewNote("");
    logAccess({ action: "view_assessment_detail", resource_type: "health_assessment", resource_id: assessment.id, metadata: { patient_id: assessment.user_id } });
    const { data } = await supabase
      .from("provider_notes")
      .select("id, note, provider_id, created_at")
      .eq("assessment_id", assessment.id)
      .order("created_at", { ascending: true });
    if (data) setNotes(data as ProviderNote[]);
  };

  /** Check if a provider's license is red-flagged (<30 days or expired) */
  const isProviderRedFlagged = (providerId: string) => {
    const cred = providerCredentialMap[providerId];
    if (!cred || cred.credential_status !== "approved") return false;
    const days = differenceInDays(parseISO(cred.license_expiry), new Date());
    return days < 30;
  };

  /** Providers eligible for patient assignment (not red-flagged) */
  const assignableProviders = allProviders.filter((p) => !isProviderRedFlagged(p.user_id));

  const assignProviderToPatient = async (patientUserId: string, providerId: string) => {
    if (isProviderRedFlagged(providerId)) {
      toast({ title: "Cannot assign", description: "This provider's license is expiring or expired. They cannot be assigned patients.", variant: "destructive" });
      return;
    }
    const { error } = await supabase
      .from("profiles")
      .update({ assigned_provider_id: providerId })
      .eq("user_id", patientUserId);
    if (error) {
      console.error("Provider assignment failed:", error);
      toast({ title: "Error", description: "Could not assign provider.", variant: "destructive" });
      return;
    }
    setAssignedProviderMap((prev) => ({ ...prev, [patientUserId]: providerId }));
    const provider = allProviders.find((p) => p.user_id === providerId);
    const provName = formatProviderName(provider);
    logAccess({ action: "assign_provider", resource_type: "profile", metadata: { patient_user_id: patientUserId, provider_id: providerId } });
    toast({ title: "Provider assigned", description: `${provName} is now this patient's provider.` });
  };

  const updateStatus = async (assessment: Assessment, newStatus: string) => {
    const oldStatus = assessment.status;
    if (oldStatus === newStatus) return;

    const { error } = await supabase.from("health_assessments").update({ status: newStatus }).eq("id", assessment.id);
    if (error) {
      toast({ title: "Error", description: "Could not update status.", variant: "destructive" });
      return;
    }

    setAssessments((prev) => prev.map((a) => a.id === assessment.id ? { ...a, status: newStatus } : a));
    if (selectedAssessment?.id === assessment.id) {
      setSelectedAssessment((prev) => prev ? { ...prev, status: newStatus } : null);
    }

    // Update reviewer map for this assessment
    if (user && !reviewerMap[assessment.id]) {
      const provName = formatProviderName(profile);
      setReviewerMap((prev) => ({ ...prev, [assessment.id]: provName }));
    }

    logAccess({ action: "update_status", resource_type: "health_assessment", resource_id: assessment.id, metadata: { old_status: oldStatus, new_status: newStatus } });
    toast({ title: "Status updated", description: `Set to ${newStatus}` });

    // Send email + in-app notification for reviewed/approved/rejected
    if (["reviewed", "approved", "rejected"].includes(newStatus)) {
      const statusMessages: Record<string, { title: string; message: string }> = {
        reviewed: {
          title: "Assessment Reviewed — Schedule Your Consultation",
          message: "A provider has reviewed your health assessment. Please schedule a consultation to discuss your personalized treatment plan.",
        },
        approved: {
          title: "Assessment Approved!",
          message: "Great news — your assessment has been approved. You're ready to proceed with treatment.",
        },
        rejected: {
          title: "Assessment Update",
          message: "Your assessment was not approved at this time. Please contact our care team for details.",
        },
      };

      const notif = statusMessages[newStatus];

      try {
        const { data: patientProfile } = await supabase
          .from("profiles")
          .select("first_name")
          .eq("user_id", assessment.user_id)
          .single();

        // Create in-app notification
        await supabase.from("notifications").insert({
          user_id: assessment.user_id,
          title: notif.title,
          message: notif.message,
          type: "assessment_update",
          related_id: assessment.id,
        });

        // Send email notification
        await supabase.functions.invoke("send-transactional-email", {
          body: {
            templateName: "assessment-status-update",
            recipientUserId: assessment.user_id,
            idempotencyKey: `assessment-status-${assessment.id}-${newStatus}`,
            notificationCategory: "assessment_updates",
            templateData: {
              patientName: patientProfile?.first_name || undefined,
              newStatus,
              goal: assessment.goal || undefined,
            },
          },
        });
      } catch (err) {
        console.error("Failed to send status notification:", err);
      }
    }
  };

  const addNote = async () => {
    if (!newNote.trim() || !selectedAssessment || !user) return;
    setIsSubmitting(true);
    const { error } = await supabase.from("provider_notes").insert({
      assessment_id: selectedAssessment.id,
      provider_id: user.id,
      note: newNote.trim(),
    });
    if (!error) {
      setNewNote("");
      const { data } = await supabase
        .from("provider_notes")
        .select("id, note, provider_id, created_at")
        .eq("assessment_id", selectedAssessment.id)
        .order("created_at", { ascending: true });
      if (data) setNotes(data as ProviderNote[]);

      // Update reviewer map if this is the first note
      if (!reviewerMap[selectedAssessment.id] && profile) {
        const provName = formatProviderName(profile);
        setReviewerMap((prev) => ({ ...prev, [selectedAssessment.id]: provName }));
      }

      logAccess({ action: "add_provider_note", resource_type: "provider_note", resource_id: selectedAssessment.id });
      toast({ title: "Note added" });
    }
    setIsSubmitting(false);
  };

  const sendScheduleReminder = async (patientUserId: string, assessmentId: string) => {
    if (!user) return;
    setSendingReminder(patientUserId);
    try {
      const provName = formatProviderName(profile) || undefined;
      const assessment = assessments.find((a) => a.id === assessmentId);
      await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "schedule-consultation-reminder",
          recipientUserId: patientUserId,
          idempotencyKey: `sched-reminder-${assessmentId}-${Date.now()}`,
          notificationCategory: "appointment_reminders",
          templateData: {
            patientName: patientProfiles[patientUserId]?.first_name || undefined,
            providerName: provName,
            goal: assessment?.goal || undefined,
          },
        },
      });
      // Also create an in-app notification
      await supabase.from("notifications").insert({
        user_id: patientUserId,
        title: "Reminder: Schedule Your Consultation",
        message: "Your provider is ready to see you. Please schedule your consultation to continue your care plan.",
        type: "appointment_reminder",
        related_id: assessmentId,
      });
      setLastReminderMap((prev) => ({ ...prev, [patientUserId]: new Date().toISOString() }));
      toast({ title: "Reminder sent", description: "The patient has been notified to schedule their consultation." });
    } catch (err) {
      console.error("Failed to send schedule reminder:", err);
      toast({ title: "Error", description: "Failed to send reminder.", variant: "destructive" });
    } finally {
      setSendingReminder(null);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const getProviderName = (providerId: string | null) => {
    if (!providerId) return null;
    const provider = allProviders.find((p) => p.user_id === providerId);
    return formatProviderName(provider);
  };

  const isOverdueAssessment = (a: Assessment) =>
    ["approved", "reviewed"].includes(a.status) &&
    !consultationCompletedMap[a.user_id] &&
    !appointmentScheduledMap[a.user_id] &&
    (Date.now() - new Date(a.updated_at).getTime()) >= overdueThreshold * 86400000;

  const filteredAssessments = activeTab === "all"
    ? assessments
    : activeTab === "unassigned"
      ? assessments.filter((a) => !assignedProviderMap[a.user_id])
      : activeTab === "mine"
        ? assessments.filter((a) => assignedProviderMap[a.user_id] === user?.id)
        : activeTab === "overdue"
          ? assessments.filter(isOverdueAssessment)
          : assessments.filter((a) => a.status === activeTab);

  const displayName = profile
    ? formatProviderName(profile)
    : user?.email;

  const stats = {
    total: assessments.length,
    unassigned: assessments.filter((a) => !assignedProviderMap[a.user_id]).length,
    mine: assessments.filter((a) => assignedProviderMap[a.user_id] === user?.id).length,
    pending: assessments.filter((a) => a.status === "pending").length,
    overdue: assessments.filter(isOverdueAssessment).length,
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-16">
        <SidebarProvider>
          <div className="min-h-[calc(100vh-4rem)] flex w-full">
            <ProviderSidebar activeTab={dashboardTab} onNavigateToTab={setDashboardTab} />
            <main className="flex-1 overflow-auto">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <SidebarTrigger className="shrink-0">
                    <PanelLeft size={18} />
                  </SidebarTrigger>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Stethoscope size={18} className="text-primary" />
                    </div>
                    <div>
                      <h1 className="font-display text-2xl text-foreground leading-tight">Provider Dashboard</h1>
                      <p className="text-muted-foreground text-sm">Welcome, {displayName}</p>
                      {dashboardTab !== "schedule" && (
                        <nav className="flex items-center gap-1.5 text-xs mt-0.5">
                          <button
                            onClick={() => setDashboardTab("schedule")}
                            className="text-muted-foreground hover:text-primary transition-colors font-medium"
                          >
                            Schedule
                          </button>
                          <BreadcrumbChevron size={12} className="text-muted-foreground/50" />
                          <span className="text-foreground font-medium">
                            {TAB_LABELS[dashboardTab] || dashboardTab}
                          </span>
                        </nav>
                      )}
                    </div>
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    <Button variant="outline" onClick={() => navigate("/therapies")} className="gap-2" size="sm">
                      <FlaskConical size={14} /> Therapy Library
                    </Button>
                    <Button variant="outline" onClick={handleSignOut} className="gap-2" size="sm">
                      <LogOut size={14} /> Sign Out
                    </Button>
                  </div>
                </div>

                {/* Onboarding Checklist */}
                <ProviderOnboardingChecklist
                  onNavigate={(section) => {
                    if (section === "profile") {
                      navigate("/provider/profile");
                    } else if (section === "availability") {
                      setDashboardTab("availability");
                    } else if (section === "patients") {
                      setDashboardTab("patients");
                    }
                  }}
                />

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                  {[
                    { label: "Total Patients", value: stats.total, icon: Users, color: "text-foreground" },
                    { label: "Unassigned", value: stats.unassigned, icon: Clock, color: "text-yellow-600" },
                    { label: "My Patients", value: stats.mine, icon: UserCheck, color: "text-primary" },
                    { label: "Pending Status", value: stats.pending, icon: CheckCircle, color: "text-blue-600" },
                    { label: "Overdue", value: stats.overdue, icon: AlertTriangle, color: stats.overdue > 0 ? "text-destructive" : "text-muted-foreground" },
                  ].map((stat) => (
                    <Card
                      key={stat.label}
                      className={`border-border bg-card ${stat.label === "Overdue" && stats.overdue > 0 ? "border-destructive/30 bg-destructive/5 cursor-pointer hover:bg-destructive/10 transition-colors" : ""}`}
                      onClick={stat.label === "Overdue" && stats.overdue > 0 ? () => { setDashboardTab("patients"); setActiveTab("overdue"); } : undefined}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-muted-foreground font-medium">{stat.label}</p>
                            <p className={`text-2xl font-bold mt-1 ${stat.color}`}>{stat.value}</p>
                          </div>
                          <stat.icon size={20} className={stat.label === "Overdue" && stats.overdue > 0 ? "text-destructive" : "text-muted-foreground"} />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Schedule tab — default landing */}
                {dashboardTab === "schedule" && (
                  <ProviderTodaySchedule
                    onNavigateToAvailability={() => setDashboardTab("availability")}
                  />
                )}

                {/* Content based on active sidebar tab */}
                {dashboardTab === "patients" && (
                  <Card id="patient-queue" className="border-border bg-card">
                    <CardContent className="p-0">
                      <Tabs value={activeTab} onValueChange={setActiveTab}>
                        <div className="px-6 pt-5 pb-0 border-b border-border">
                          <div className="flex items-center justify-between mb-4">
                            <h2 className="font-display text-xl text-foreground">Patient Queue</h2>
                            <div className="flex items-center gap-2">
                              <Label htmlFor="overdue-threshold" className="text-xs text-muted-foreground whitespace-nowrap">Overdue after</Label>
                              <Select
                                value={String(overdueThreshold)}
                                onValueChange={async (val) => {
                                  const days = parseInt(val);
                                  setOverdueThreshold(days);
                                  if (user) {
                                    const { error } = await supabase
                                      .from("profiles")
                                      .update({ overdue_threshold_days: days } as any)
                                      .eq("user_id", user.id);
                                    if (error) {
                                      toast({ title: "Error", description: "Failed to save threshold.", variant: "destructive" });
                                    } else {
                                      toast({ title: "Saved", description: `Overdue threshold set to ${days} day${days !== 1 ? "s" : ""}.` });
                                    }
                                  }
                                }}
                              >
                                <SelectTrigger id="overdue-threshold" className="h-7 w-[80px] text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {[1, 2, 3, 5, 7, 10, 14].map((d) => (
                                    <SelectItem key={d} value={String(d)} className="text-xs">{d} day{d !== 1 ? "s" : ""}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <ScrollFadeWrapper>
                          <TabsList className="flex overflow-x-auto no-scrollbar h-auto gap-1 p-1 bg-muted w-full justify-start">
                            <TabsTrigger value="all" className="shrink-0 min-h-11">All ({stats.total})</TabsTrigger>
                            <TabsTrigger value="unassigned" className="shrink-0 min-h-11">Unassigned ({stats.unassigned})</TabsTrigger>
                            <TabsTrigger value="mine" className="shrink-0 min-h-11">My Patients ({stats.mine})</TabsTrigger>
                            <TabsTrigger value="pending" className="shrink-0 min-h-11">Pending</TabsTrigger>
                            <TabsTrigger value="overdue" className="shrink-0 min-h-11 gap-1">
                              <AlertTriangle size={12} />
                              Overdue ({stats.overdue})
                            </TabsTrigger>
                          </TabsList>
                          </ScrollFadeWrapper>
                        </div>

                        <TabsContent value={activeTab} className="mt-0">
                          {filteredAssessments.length === 0 ? (
                            <div className="p-12 text-center text-muted-foreground">
                              <Users size={40} className="mx-auto mb-3 opacity-50" />
                              <p>No patients in this category.</p>
                            </div>
                          ) : (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Patient</TableHead>
                                  <TableHead>Date</TableHead>
                                  <TableHead>Goal</TableHead>
                                  <TableHead>Peptide Interest</TableHead>
                                  <TableHead>Flags</TableHead>
                                  <TableHead>Status</TableHead>
                                  <TableHead>Provider</TableHead>
                                  <TableHead>Reviewed By</TableHead>
                                  <TableHead className="text-right">Next Step</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {filteredAssessments.map((a) => {
                                  const nextStep = getNextStep(a, !!reviewerMap[a.id], prescriptionMap[a.id], !!assignedProviderMap[a.user_id]);
                                  const glp1Flags = getGlp1Flags(a.conditions, a.additional_info);
                                  const isOverdueForBooking = isOverdueAssessment(a);
                                  return (
                                    <TableRow key={a.id} className={`cursor-pointer hover:bg-muted/50 ${isOverdueForBooking ? "bg-destructive/5" : ""}`} onClick={() => openAssessment(a)}>
                                      <TableCell className="text-sm font-medium">
                                        <span className="flex items-center gap-1.5">
                                          {getPatientName(a.user_id)}
                                          {isOverdueForBooking && (
                                            <span className="relative flex h-2 w-2" title="Overdue for consultation booking">
                                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75" />
                                              <span className="relative inline-flex rounded-full h-2 w-2 bg-destructive" />
                                            </span>
                                          )}
                                        </span>
                                      </TableCell>
                                      <TableCell className="text-sm text-muted-foreground">{new Date(a.created_at).toLocaleDateString()}</TableCell>
                                      <TableCell className="text-sm">{a.goal || "—"}</TableCell>
                                      <TableCell className="text-sm">{a.peptide_interest || "—"}</TableCell>
                                      <TableCell>
                                        <div className="flex flex-wrap gap-1">
                                          {glp1Flags.length > 0 && (
                                            <Badge variant="destructive" className="text-xs gap-1">
                                              <ShieldAlert size={12} /> GLP-1
                                            </Badge>
                                          )}
                                          {isOverdueForBooking && (
                                            <Badge className="text-[10px] gap-1 bg-destructive/10 text-destructive border-destructive/20">
                                              <Clock size={10} /> Overdue
                                            </Badge>
                                          )}
                                          {!glp1Flags.length && !isOverdueForBooking && (
                                            <span className="text-xs text-muted-foreground">—</span>
                                          )}
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <Badge variant="secondary" className={`capitalize text-xs ${statusConfig[a.status] || ""}`}>
                                          {a.status}
                                        </Badge>
                                      </TableCell>
                                      <TableCell className="text-sm">
                                        {assignedProviderMap[a.user_id]
                                          ? formatProviderName(allProviders.find((p) => p.user_id === assignedProviderMap[a.user_id]))
                                          : "—"}
                                      </TableCell>
                                      <TableCell className="text-sm text-muted-foreground">
                                        {reviewerMap[a.id] || "—"}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        {nextStep.label === "Write Prescription" ? (
                                          <Badge
                                            variant="outline"
                                            className="text-xs gap-1 cursor-pointer hover:bg-primary/10 transition-colors"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setPrescribeAssessment(a);
                                            }}
                                          >
                                            <nextStep.icon size={12} /> {nextStep.label}
                                          </Badge>
                                        ) : (
                                          <Badge variant="outline" className="text-xs gap-1">
                                            <nextStep.icon size={12} /> {nextStep.label}
                                          </Badge>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          )}
                        </TabsContent>
                      </Tabs>
                    </CardContent>
                  </Card>
                )}

                {dashboardTab === "appointments" && (
                  <ErrorBoundary label="Appointments">
                    <div className="space-y-8">
                      <ProviderCalendarView />
                      <ProviderAppointments />
                    </div>
                  </ErrorBoundary>
                )}

                {dashboardTab === "payouts" && (
                  <ErrorBoundary label="Payouts">
                    <ProviderPayoutView />
                  </ErrorBoundary>
                )}

                {dashboardTab === "tax" && (
                  <ErrorBoundary label="Tax info">
                    <ContractorTaxInfo />
                  </ErrorBoundary>
                )}

                {dashboardTab === "availability" && (
                  <ErrorBoundary label="Availability">
                    <ProviderAvailability />
                  </ErrorBoundary>
                )}

                {dashboardTab === "messages" && (
                  <ErrorBoundary label="Messages">
                    <MessageInbox />
                  </ErrorBoundary>
                )}

                {dashboardTab === "reference" && (
                  <ErrorBoundary label="Peptide reference">
                    <PeptideReference />
                  </ErrorBoundary>
                )}
              </div>
            </main>
          </div>
        </SidebarProvider>
      </div>

      <Dialog open={!!selectedAssessment} onOpenChange={(open) => { if (!open) setSelectedAssessment(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedAssessment && (() => {
            const nextStep = getNextStep(selectedAssessment, notes.length > 0, prescriptionMap[selectedAssessment.id], !!assignedProviderMap[selectedAssessment.user_id]);
            const glp1Flags = getGlp1Flags(selectedAssessment.conditions, selectedAssessment.additional_info);
            return (
              <>
                {glp1Flags.length > 0 && (
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20 mb-2">
                    <ShieldAlert size={20} className="text-destructive shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-destructive">GLP-1 Eligibility Concern</p>
                      <p className="text-xs text-destructive/80 mt-1">
                        This patient reported: {glp1Flags.join(", ")}. Review carefully before prescribing GLP-1 medications.
                      </p>
                    </div>
                  </div>
                )}
                <DialogHeader>
                  <DialogTitle className="font-display text-xl">
                    {getPatientName(selectedAssessment.user_id)}
                  </DialogTitle>
                  <p className="text-sm text-muted-foreground">
                    Submitted {new Date(selectedAssessment.created_at).toLocaleDateString()}
                    {reviewerMap[selectedAssessment.id] && (
                      <> · Reviewed by {reviewerMap[selectedAssessment.id]}</>
                    )}
                  </p>
                </DialogHeader>

                {/* Workflow Progress Bar */}
                <WorkflowProgressBar
                  triageLevel={assignedProviderMap[selectedAssessment.user_id] ? "assigned" : null}
                  status={selectedAssessment.status}
                  hasNotes={notes.length > 0}
                  hasCompletedConsultation={consultationCompletedMap[selectedAssessment.user_id] || false}
                  hasScheduledConsultation={appointmentScheduledMap[selectedAssessment.user_id] || false}
                  hasPrescription={prescriptionMap[selectedAssessment.id] || false}
                  scheduledTime={scheduledTimeMap[selectedAssessment.user_id] || undefined}
                  reviewedAt={selectedAssessment.status !== "pending" ? selectedAssessment.updated_at : undefined}
                  createdAt={selectedAssessment.created_at}
                />

                {/* Next Step Banner */}
                {selectedAssessment.status !== "rejected" && nextStep.label !== "Complete" && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/10">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <nextStep.icon size={16} className="text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground">Next: {nextStep.label}</p>
                      <p className="text-xs text-muted-foreground">{nextStep.description}</p>
                    </div>
                  </div>
                )}

                <div className="space-y-6 mt-4">
                  {/* Provider Assignment & Status selectors */}
                  <div className="flex items-center gap-6 flex-wrap">
                    <div className="flex items-center gap-4">
                      <label className="text-sm font-medium text-foreground">Assigned Provider:</label>
                      <Select
                        value={assignedProviderMap[selectedAssessment.user_id] || ""}
                        onValueChange={(v) => assignProviderToPatient(selectedAssessment.user_id, v)}
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="Select provider" />
                        </SelectTrigger>
                        <SelectContent>
                          {assignableProviders.map((p) => (
                            <SelectItem key={p.user_id} value={p.user_id}>
                              Dr. {p.last_name || p.first_name || "Unknown"}
                            </SelectItem>
                          ))}
                          {assignableProviders.length === 0 && (
                            <div className="px-3 py-2 text-xs text-muted-foreground">No eligible providers available</div>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="text-sm font-medium text-foreground">Status:</label>
                      <Select
                        value={selectedAssessment.status}
                        onValueChange={(v) => updateStatus(selectedAssessment, v)}
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="reviewed">Reviewed</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Clinical details */}
                  <div className="grid gap-4">
                    {[
                      { label: "Primary Goal", value: selectedAssessment.goal },
                      { label: "Peptide Interest", value: selectedAssessment.peptide_interest },
                      { label: "Current Medications", value: selectedAssessment.medications },
                      { label: "Allergies", value: selectedAssessment.allergies },
                      { label: "Medical Conditions", value: selectedAssessment.conditions },
                      { label: "Additional Info", value: selectedAssessment.additional_info },
                    ].map((item) => (
                      <div key={item.label} className="p-3 rounded-lg bg-muted/50">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">{item.label}</p>
                        <p className="text-sm text-foreground">{item.value || "Not provided"}</p>
                      </div>
                    ))}
                  </div>

                  {/* Provider Notes */}
                  <div>
                    <h3 className="font-semibold text-foreground text-sm mb-3">Clinical Notes</h3>
                    {notes.length > 0 ? (
                      <div className="space-y-2 mb-4">
                        {notes.map((n) => (
                          <div key={n.id} className="p-3 rounded-lg border border-border bg-card">
                            <p className="text-sm text-foreground">{n.note}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(n.created_at).toLocaleString()}
                              {patientProfiles[n.provider_id] && (
                                <> · Dr. {patientProfiles[n.provider_id]?.last_name || patientProfiles[n.provider_id]?.first_name}</>
                              )}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground mb-4 italic">No notes yet.</p>
                    )}
                    <div className="flex gap-2">
                      <Textarea
                        placeholder="Add a clinical note..."
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        className="min-h-[80px]"
                      />
                      <Button onClick={addNote} disabled={isSubmitting || !newNote.trim()} size="icon" className="shrink-0 self-end">
                        <Send size={16} />
                      </Button>
                    </div>
                    </div>

                  {/* Session Finalization Checklist */}
                  {notes.length > 0 && selectedAssessment.status !== "rejected" && (
                    <SessionFinalizationChecklist
                      assessmentId={selectedAssessment.id}
                      providerId={user?.id || ""}
                    />
                  )}
                  
                  {/* Consultation Status */}
                  {(selectedAssessment.status === "reviewed" || selectedAssessment.status === "approved") && (
                    <div>
                      <h3 className="font-semibold text-foreground text-sm mb-3">Consultation</h3>
                      {consultationCompletedMap[selectedAssessment.user_id] ? (
                        <div className="flex flex-col items-center gap-3 py-4 px-4 rounded-lg bg-primary/5 border border-primary/20">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <CheckCircle size={20} className="text-primary" />
                          </div>
                          <p className="font-semibold text-foreground text-sm">Consultation Completed</p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-3 py-6 px-4 rounded-lg bg-muted/50 border border-border">
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                            {appointmentScheduledMap[selectedAssessment.user_id]
                              ? <Calendar size={24} className="text-primary" />
                              : <Clock size={24} className="text-muted-foreground" />
                            }
                          </div>
                          <p className="font-semibold text-foreground">
                            {appointmentScheduledMap[selectedAssessment.user_id]
                              ? "Consultation Scheduled"
                              : "Awaiting Consultation Booking"
                            }
                          </p>
                          <p className="text-sm text-muted-foreground text-center">
                            {appointmentScheduledMap[selectedAssessment.user_id]
                              ? "The patient has an appointment scheduled. Once completed, you can proceed."
                              : "The patient hasn't scheduled their consultation yet. You can send them a reminder."
                            }
                          </p>
                          {!appointmentScheduledMap[selectedAssessment.user_id] && (
                            <div className="flex flex-col items-center gap-1 mt-1">
                              <Button
                                variant="outline"
                                size="sm"
                                className="gap-2"
                                disabled={sendingReminder === selectedAssessment.user_id}
                                onClick={() => sendScheduleReminder(selectedAssessment.user_id, selectedAssessment.id)}
                              >
                                <Mail size={14} />
                                {sendingReminder === selectedAssessment.user_id ? "Sending..." : "Send Scheduling Reminder"}
                              </Button>
                              {lastReminderMap[selectedAssessment.user_id] && (
                                <span className="text-xs text-muted-foreground">
                                  Last sent {new Date(lastReminderMap[selectedAssessment.user_id]).toLocaleDateString("en-US", { month: "short", day: "numeric" })} at {new Date(lastReminderMap[selectedAssessment.user_id]).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Prescription — inline form when at Prescribe step, button otherwise */}
                  <div>
                    <h3 className="font-semibold text-foreground text-sm mb-3">Prescribe</h3>
                    {prescriptionMap[selectedAssessment.id] ? (
                      <div className="flex flex-col items-center gap-3 py-6 px-4 rounded-lg bg-primary/5 border border-primary/20">
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <CheckCircle size={24} className="text-primary" />
                        </div>
                        <p className="font-semibold text-foreground">Prescription Sent</p>
                        <p className="text-sm text-muted-foreground text-center">
                          The prescription has been sent to the pharmacy. The patient will be notified.
                        </p>
                      </div>
                    ) : selectedAssessment.status === "approved" && consultationCompletedMap[selectedAssessment.user_id] ? (
                      <SendPrescription
                        patientId={selectedAssessment.user_id}
                        assessmentId={selectedAssessment.id}
                        defaultPeptide={selectedAssessment.peptide_interest || undefined}
                        inline
                        onPrescriptionSent={() => {
                          setPrescriptionMap((prev) => ({ ...prev, [selectedAssessment.id]: true }));
                          fetchAssessments();
                        }}
                      />
                    ) : (
                      <p className="text-sm text-muted-foreground italic">
                        {selectedAssessment.status === "approved" && !consultationCompletedMap[selectedAssessment.user_id]
                          ? "Complete the consultation first to write a prescription."
                          : "Approve this assessment first to write a prescription."}
                      </p>
                    )}
                  </div>

                  {/* Care Assignment */}
                  <div>
                    <h3 className="font-semibold text-foreground text-sm mb-3">Care Assignment</h3>
                    <div className="space-y-4 p-4 rounded-lg border border-border bg-card">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-foreground">Assigned Provider</p>
                          <p className="text-sm text-muted-foreground">
                            {assignedProviderMap[selectedAssessment.user_id]
                              ? getProviderName(assignedProviderMap[selectedAssessment.user_id])
                              : "Not assigned — use the dropdown above"}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Last Checkup Date</Label>
                        <div className="flex items-center gap-3">
                          <Input
                            type="date"
                            className="w-48"
                            value={lastCheckupMap[selectedAssessment.user_id] || ""}
                            onChange={(e) => {
                              setLastCheckupMap((prev) => ({ ...prev, [selectedAssessment.user_id]: e.target.value }));
                            }}
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1.5"
                            onClick={() => {
                              const date = lastCheckupMap[selectedAssessment.user_id];
                              if (date) recordCheckup(selectedAssessment.user_id, date);
                            }}
                            disabled={!lastCheckupMap[selectedAssessment.user_id]}
                          >
                            <Calendar size={14} /> Save
                          </Button>
                          <Button
                            size="sm"
                            className="gap-1.5"
                            onClick={() => {
                              const today = new Date().toISOString().split("T")[0];
                              recordCheckup(selectedAssessment.user_id, today);
                            }}
                          >
                            <CheckCircle size={14} /> Today
                          </Button>
                        </div>
                        {lastCheckupMap[selectedAssessment.user_id] && (
                          <p className="text-xs text-muted-foreground">
                            Last seen: {new Date(lastCheckupMap[selectedAssessment.user_id]!).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Quick Prescription Dialog from Queue */}
      <Dialog open={!!prescribeAssessment} onOpenChange={(open) => { if (!open) setPrescribeAssessment(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {prescribeAssessment && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl flex items-center gap-2">
                  <Pill size={20} className="text-primary" />
                  Write Prescription — {getPatientName(prescribeAssessment.user_id)}
                </DialogTitle>
                <p className="text-sm text-muted-foreground">
                  {prescribeAssessment.peptide_interest && <>Interest: {prescribeAssessment.peptide_interest} · </>}
                  Assessment approved {new Date(prescribeAssessment.updated_at).toLocaleDateString()}
                </p>
              </DialogHeader>
              {prescriptionMap[prescribeAssessment.id] ? (
                <div className="flex flex-col items-center gap-3 py-6 px-4 rounded-lg bg-primary/5 border border-primary/20">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <CheckCircle size={24} className="text-primary" />
                  </div>
                  <p className="font-semibold text-foreground">Prescription Already Sent</p>
                </div>
              ) : !consultationCompletedMap[prescribeAssessment.user_id] ? (
                <div className="flex flex-col items-center gap-3 py-6 px-4 rounded-lg bg-muted/50 border border-border">
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                    <Clock size={24} className="text-muted-foreground" />
                  </div>
                  <p className="font-semibold text-foreground">Consultation Required</p>
                  <p className="text-sm text-muted-foreground text-center">
                    Complete the consultation with this patient before writing a prescription.
                  </p>
                </div>
              ) : (
                <SendPrescription
                  patientId={prescribeAssessment.user_id}
                  assessmentId={prescribeAssessment.id}
                  defaultPeptide={prescribeAssessment.peptide_interest || undefined}
                  inline
                  onPrescriptionSent={() => {
                    setPrescriptionMap((prev) => ({ ...prev, [prescribeAssessment.id]: true }));
                    fetchAssessments();
                    setPrescribeAssessment(null);
                  }}
                />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ProviderDashboardWithGate = () => (
  <ProviderOnboardingGate>
    <ProviderDashboard />
  </ProviderOnboardingGate>
);

export default ProviderDashboardWithGate;
